import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  X,
  Info,
  Users,
  UserRoundCog,
  Search,
  UserRoundPlus,
  BarChart2,
} from 'lucide-react';
import '../CSS/GuideModal.css';

const StudentsGuideModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setIsOpen(false);
  };

  return (
    <>
      {/* ── Floating Action Button ── */}
      <div className="gml-fab-wrapper">
        <span className="gml-fab-tooltip">Need help?</span>
        <button
          className="gml-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Open Students Guide"
          title="Student Management Guide"
        >
          <BookOpen className="gml-fab-icon" />
        </button>
      </div>

      {/* ── Modal ── */}
      {isOpen && (
        <div
          className="gml-backdrop"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gml-modal-title"
        >
          <div className="gml-modal">

            {/* Header */}
            <div className="gml-modal-header">
              <div className="gml-modal-title-wrap">
                <BookOpen className="gml-modal-header-icon" />
                <h2 className="gml-modal-title" id="gml-modal-title">
                  Students Module Guide
                </h2>
              </div>
              <button
                className="gml-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close guide"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="gml-modal-body">

              {/* Welcome Banner */}
              <div className="gml-welcome-banner">
                <Info className="gml-welcome-icon" />
                <div>
                  <p className="gml-welcome-heading">
                    Welcome to the Students Module!
                  </p>
                  <p className="gml-welcome-text">
                    This comprehensive guide will help you navigate and utilize
                    all features of the Students component effectively. Use it
                    anytime you need a quick reference.
                  </p>
                </div>
              </div>

              {/* ── Key Features Overview ── */}
              <h3 className="gml-section-heading">Key Features Overview</h3>
              <div className="gml-cards-grid">

                {/* Student Records */}
                <div className="gml-feature-card gml-feature-card--blue">
                  <div className="gml-card-head gml-card-head--blue">
                    <Users className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Student Records</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">1</span>
                        View all student records with comprehensive details
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">2</span>
                        Advanced search and filtering capabilities
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">3</span>
                        Sort students by name, class, or date of birth
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Student Operations */}
                <div className="gml-feature-card gml-feature-card--green">
                  <div className="gml-card-head gml-card-head--green">
                    <UserRoundCog className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Student Operations</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">1</span>
                        Add new students with complete profile information
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">2</span>
                        Edit existing student records
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">3</span>
                        Delete student records when needed
                      </li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Detailed Feature Guide ── */}
              <h3 className="gml-section-heading">Detailed Feature Guide</h3>
              <div className="gml-detail-section">

                {/* Working with Student Records */}
                <div className="gml-detail-card gml-detail-card--cyan">
                  <div className="gml-detail-head gml-detail-head--cyan">
                    <Search className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Working with Student Records</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--cyan">Search and Filter</p>
                    <ul className="gml-detail-list">
                      <li>Use the search bar to find students by any field (name, class, village, etc.)</li>
                      <li>The system searches across all student fields simultaneously</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Sorting Options</p>
                    <ul className="gml-detail-list">
                      <li><strong>Name:</strong> Sort alphabetically (A–Z or Z–A)</li>
                      <li><strong>Date of Birth:</strong> Sort chronologically (oldest–youngest or vice versa)</li>
                      <li><strong>Class:</strong> Sort numerically (Class 6–12 or 12–6)</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Student Actions</p>
                    <ul className="gml-detail-list">
                      <li>Click the edit (pencil) button to modify student details</li>
                      <li>Click the delete (trash) button to remove a student record</li>
                      <li>Editing a student scrolls automatically to the form section</li>
                    </ul>
                  </div>
                </div>

                {/* Adding and Editing Students */}
                <div className="gml-detail-card gml-detail-card--yellow">
                  <div className="gml-detail-head gml-detail-head--yellow">
                    <UserRoundPlus className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Adding and Editing Students</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--yellow">Required Fields</p>
                    <ul className="gml-detail-list">
                      <li><strong>Student Name:</strong> Must contain only letters and spaces</li>
                      <li><strong>Date of Birth:</strong> Must be a valid date</li>
                      <li><strong>Gender:</strong> Must be selected (Male / Female)</li>
                      <li><strong>Class:</strong> Must be selected (6–12)</li>
                      <li><strong>Contact Information:</strong> Phone numbers must be at least 8 digits</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Form Validation</p>
                    <ul className="gml-detail-list">
                      <li>All required fields are clearly marked</li>
                      <li>Invalid inputs show immediate feedback</li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--muted">Duplicate check</span>
                        — Duplicate student names are automatically detected
                      </li>
                      <li>Password requirements enforced (8+ characters with allowed symbols)</li>
                    </ul>
                  </div>
                </div>

                {/* Advanced Features */}
                <div className="gml-detail-card gml-detail-card--green">
                  <div className="gml-detail-head gml-detail-head--green">
                    <BarChart2 className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Advanced Features</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--green">Statistics</p>
                    <ul className="gml-detail-list">
                      <li>View overall student counts by gender</li>
                      <li>See class-wise breakdowns of student numbers</li>
                      <li>Toggle statistics display with the Show / Hide button</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Export Options</p>
                    <ul className="gml-detail-list">
                      <li><strong>Excel:</strong> Fully formatted spreadsheet with all student data</li>
                      <li><strong>Word:</strong> Professional document with student records</li>
                      <li><strong>PDF:</strong> Print-ready document with proper pagination</li>
                      <li>All exports include the complete student record with all fields</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Data Security</p>
                    <ul className="gml-detail-list">
                      <li>Passwords are securely stored in the database</li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--success">Protected</span>
                        — Confirmation required before deleting any record
                      </li>
                      <li>Form validation prevents invalid data entry</li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Best Practices ── */}
              <div className="gml-best-practices">
                <p className="gml-best-practices-heading">Tips for Effective Use</p>
                <ul className="gml-best-practices-list">
                  {[
                    'Export data regularly for backup purposes.',
                    'Take advantage of sorting to keep student lists organised.',
                    'Use the search function to quickly locate specific students.',
                    'Double-check all information before submitting the form.',
                    'Remove records for departed students to keep data accurate.',
                  'Access this guide anytime via the floating button on the bottom-right of the screen.',
                  ].map((tip, i) => (
                    <li key={i}>
                      <span className="gml-bp-dot" aria-hidden="true" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Footer */}
            <div className="gml-modal-footer">
              <button className="gml-close-btn" onClick={() => setIsOpen(false)}>
                Got it
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default StudentsGuideModal;